import { accessibleName, computeRole, cssEscape } from "./roles.js";
function stale() {
    return { error: { code: "stale_ref", message: "element no longer exists — re-read the page" } };
}
function describe(el) {
    const role = computeRole(el);
    const name = accessibleName(el);
    return name ? `${role} "${name}"` : role;
}
/**
 * Executes interactions against live DOM elements. Uses native value setters
 * and full event sequences so framework-controlled inputs (React/Vue/Angular)
 * register changes. See docs/PLAN.md §2.2.
 */
export class ActionEngine {
    registry;
    safety;
    confirm;
    constructor(registry, safety, confirm) {
        this.registry = registry;
        this.safety = safety;
        this.confirm = confirm;
    }
    query(spec) {
        const { root } = spec;
        const limit = spec.limit ?? 10;
        let candidates = [];
        if (spec.testid) {
            candidates = Array.from(root.querySelectorAll(`[data-testid="${cssEscape(spec.testid)}"],[data-test-id="${cssEscape(spec.testid)}"],[data-test="${cssEscape(spec.testid)}"]`));
        }
        else {
            candidates = Array.from(root.querySelectorAll("*"));
        }
        const hits = [];
        for (const el of candidates) {
            if (this.safety.zoneBlocked(el))
                continue;
            const role = computeRole(el);
            const name = accessibleName(el);
            if (spec.role && role !== spec.role)
                continue;
            if (spec.name && !name.toLowerCase().includes(spec.name.toLowerCase()))
                continue;
            if (spec.text) {
                const t = (el.textContent ?? "").toLowerCase();
                if (!t.includes(spec.text.toLowerCase()))
                    continue;
                // Prefer the tightest element containing the text.
                if (Array.from(el.children).some((c) => (c.textContent ?? "").toLowerCase().includes(spec.text.toLowerCase())))
                    continue;
            }
            if (!spec.testid && role === "generic" && !name && !spec.text)
                continue;
            hits.push({
                ref: this.registry.refFor(el),
                role,
                name: name || undefined,
                testid: el.getAttribute("data-testid") ?? undefined,
                snippet: (el.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 60) || undefined,
            });
            if (hits.length >= limit)
                break;
        }
        return hits;
    }
    async click(ref) {
        const el = this.registry.resolve(ref);
        if (!el)
            return stale();
        const decision = this.safety.gateClick(el);
        const blocked = await this.safety.enforce(decision, this.confirm, `click ${describe(el)}`);
        if (blocked)
            return blocked;
        scrollIntoView(el);
        const occluder = occludedBy(el);
        if (occluder) {
            return {
                error: {
                    code: "occluded",
                    message: `element is covered by ${describe(occluder)} — handle the overlay first`,
                },
            };
        }
        dispatchPointerSequence(el);
        return { ok: true, delta: `clicked ${describe(el)}` };
    }
    async type(ref, text, mode = "replace", pressEnter = false) {
        const el = this.registry.resolve(ref);
        if (!el)
            return stale();
        if (this.safety.zoneBlocked(el))
            return { error: { code: "denied", message: "element is in a denied zone" } };
        if (this.safety.isSecretField(el)) {
            return {
                error: {
                    code: "denied",
                    message: "this is a password field — Quill won't type into it; ask the user to enter it directly",
                },
            };
        }
        const tag = el.tagName.toLowerCase();
        scrollIntoView(el);
        el.focus();
        if (tag === "input" || tag === "textarea") {
            const input = el;
            const next = mode === "append" ? (input.value ?? "") + text : text;
            setNativeValue(input, next);
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.dispatchEvent(new Event("change", { bubbles: true }));
            if (pressEnter) {
                input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
                input.dispatchEvent(new KeyboardEvent("keyup", { key: "Enter", bubbles: true }));
            }
            return { ok: true, valueAfter: input.value, delta: `typed into ${describe(el)}` };
        }
        if (el.isContentEditable) {
            if (mode === "replace")
                el.textContent = "";
            el.textContent = (el.textContent ?? "") + text;
            el.dispatchEvent(new Event("input", { bubbles: true }));
            return { ok: true, valueAfter: el.textContent ?? "", delta: `typed into ${describe(el)}` };
        }
        return { error: { code: "not_interactable", message: `${describe(el)} is not a text field` } };
    }
    async select(ref, option) {
        const el = this.registry.resolve(ref);
        if (!el)
            return stale();
        if (this.safety.zoneBlocked(el))
            return { error: { code: "denied", message: "element is in a denied zone" } };
        const tag = el.tagName.toLowerCase();
        if (tag === "select") {
            const sel = el;
            const opts = Array.from(sel.options);
            const match = (option.value != null && opts.find((o) => o.value === option.value)) ||
                (option.label != null &&
                    opts.find((o) => (o.textContent ?? "").trim().toLowerCase() === option.label.toLowerCase())) ||
                (option.label != null &&
                    opts.find((o) => (o.textContent ?? "").toLowerCase().includes(option.label.toLowerCase())));
            if (!match) {
                return {
                    error: {
                        code: "not_found",
                        message: `no option matching ${JSON.stringify(option)} in ${describe(el)}`,
                    },
                };
            }
            sel.value = match.value;
            sel.dispatchEvent(new Event("input", { bubbles: true }));
            sel.dispatchEvent(new Event("change", { bubbles: true }));
            return { ok: true, valueAfter: match.textContent?.trim() ?? match.value, delta: `selected "${match.textContent?.trim()}"` };
        }
        // ARIA listbox/radio: click the matching option/radio by label.
        const role = computeRole(el);
        if (role === "radio" || role === "checkbox" || role === "option") {
            return this.click(ref);
        }
        return { error: { code: "not_interactable", message: `${describe(el)} is not selectable` } };
    }
    scroll(spec) {
        if (spec.ref) {
            const el = this.registry.resolve(spec.ref);
            if (!el)
                return stale();
            scrollIntoView(el);
            return { ok: true, viewport: viewportDesc(el.ownerDocument) };
        }
        const win = spec.root.ownerDocument.defaultView;
        if (win) {
            const dy = spec.direction === "up" ? -win.innerHeight * 0.8 : win.innerHeight * 0.8;
            win.scrollBy({ top: dy, behavior: "auto" });
        }
        return { ok: true, viewport: viewportDesc(spec.root.ownerDocument) };
    }
}
// ── DOM helpers ──────────────────────────────────────────────────────────
function scrollIntoView(el) {
    try {
        el.scrollIntoView({ block: "center", inline: "nearest" });
    }
    catch {
        /* jsdom/happy-dom may not implement it */
    }
}
/** React/Vue-safe value setter: bypasses the framework's value tracker. */
function setNativeValue(el, value) {
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const desc = Object.getOwnPropertyDescriptor(proto, "value");
    if (desc?.set)
        desc.set.call(el, value);
    else
        el.value = value;
}
function dispatchPointerSequence(el) {
    const opts = { bubbles: true, cancelable: true, composed: true };
    const he = el;
    try {
        el.dispatchEvent(new PointerEvent("pointerdown", opts));
    }
    catch {
        /* PointerEvent may be unavailable in test env */
    }
    el.dispatchEvent(new MouseEvent("mousedown", opts));
    he.focus?.();
    try {
        el.dispatchEvent(new PointerEvent("pointerup", opts));
    }
    catch {
        /* ignore */
    }
    el.dispatchEvent(new MouseEvent("mouseup", opts));
    he.click?.();
}
function occludedBy(el) {
    const doc = el.ownerDocument;
    const rect = el.getBoundingClientRect?.();
    if (!rect || (rect.width === 0 && rect.height === 0))
        return null;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const top = doc.elementFromPoint?.(cx, cy);
    if (!top)
        return null;
    if (top === el || el.contains(top) || top.contains(el))
        return null;
    return top;
}
function viewportDesc(doc) {
    const w = doc.defaultView;
    if (!w)
        return "";
    const atTop = w.scrollY <= 0;
    const atBottom = w.scrollY + w.innerHeight >= doc.body.scrollHeight - 2;
    return `scrollY=${Math.round(w.scrollY)}${atTop ? " (top)" : ""}${atBottom ? " (bottom)" : ""}`;
}
//# sourceMappingURL=actions.js.map